import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { meetUsEvents, complianceItems, companyStats } from "@/mocks/company";

export default function Company() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Company</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              About Voxmaster.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              From a single SBC in Las Vegas to a four-PoP global fabric routing 60M+ minutes annually. We are a team of telecom engineers, carrier-relations veterans, and compliance specialists on a mission to make wholesale voice infrastructure that operators can trust.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-0 border border-background-200 rounded-xl overflow-hidden bg-background-50">
              {companyStats.map((s) => (
                <div key={s.label} className="p-5 border-b border-r border-background-200 last:border-r-0 text-center">
                  <p className="text-2xl md:text-3xl font-semibold text-foreground-950 font-heading">{s.value}</p>
                  <p className="text-xs text-foreground-600 mt-1 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Meet Us */}
        <section className="py-14 md:py-20 bg-secondary-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary-950 via-secondary-900 to-secondary-950 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-accent-400 font-semibold">Meet us</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-background-50 leading-tight">
                Come say hello at our next events.
              </h2>
              <p className="mt-3 text-background-300 max-w-3xl leading-relaxed">
                Our carrier-relations and engineering teams hit the road every quarter. Find us at these industry conferences — or book a private meeting ahead of time.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {meetUsEvents.map((e) => (
                <div key={e.name} className="rounded-xl border border-secondary-800 bg-secondary-900/60 p-6 md:p-8 flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span className="w-14 h-14 rounded-lg bg-accent-500/15 text-accent-400 flex items-center justify-center shrink-0">
                        <i className={`${e.icon} text-2xl w-7 h-7 flex items-center justify-center`} />
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-background-50 font-heading">{e.name}</h3>
                        <p className="text-sm text-background-300">{e.venue}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-background-300">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-calendar-line w-4 h-4 flex items-center justify-center text-accent-400" />
                      {e.dates}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-accent-400" />
                      {e.city}
                    </span>
                  </div>

                  <p className="mt-4 text-sm text-background-300 leading-relaxed flex-1">{e.desc}</p>

                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-5 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Book a meeting
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section id="compliance" className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Compliance &amp; security</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Licensed. Audited. Transparent.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                We operate under the regulatory frameworks of every jurisdiction we serve. Our compliance posture is not a checkbox exercise — it is core infrastructure, reviewed quarterly.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {complianceItems.map((c) => (
                <div key={c.title} className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-950 font-heading">{c.title}</h3>
                      <span className="text-[11px] text-accent-600 font-medium">{c.license}</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-primary-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/40 to-transparent pointer-events-none" />
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-background-50/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-background-50 font-heading leading-tight">
              Let us show you the network.
            </h2>
            <p className="mt-4 text-background-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Set up a test trunk, meet the team, and see our quality dashboards and compliance documentation in real time. All before you route a single paid minute.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-background-50 text-primary-700 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
              >
                Start a test
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}